import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  getDocument,
  GlobalWorkerOptions,
  PDFDocumentProxy,
  RenderTask,
} from "pdfjs-dist";
import { ChromePicker } from "react-color";
import type { ColorResult } from "react-color";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import "../styles/PDFPositionTool.css";
import { toast } from "react-toastify";
import { PDF_SCALE, PDF_FONT_SIZE_OFFSET, Y_OFFSET_CORRECTION } from "../constants/pdfConfig";
import { Y_OFFSET_TOOL_DISPLAY } from "../constants/fdaConfig";
import { mmToPixels } from "../utils/units";
import { useNavigate, useLocation } from "react-router-dom";
import { logUserAction } from "../utils/logUtils";

GlobalWorkerOptions.workerSrc = 
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";

interface PDFOption {
  url: string;
  fields: {
    [source: string]: string[];
  };
  firestoreDoc: string;
}

interface PDFPositionToolProps {
  pdfOptions: {
    [pdfName: string]: PDFOption;
  };
  defaultPdfName?: string;
}

type FieldData = {
  x: number;
  y: number;
  size?: number;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  customText?: string; // Nouveau champ pour le texte personnalisé
  isCustomField?: boolean; // Indique si c'est un champ personnalisé
  order?: number; // Ordre d'affichage pour la réorganisation
};

type PDFCoordinatesByPage = Record<string, Record<string, FieldData>>;

const PDFPositionTool: React.FC<PDFPositionToolProps> = ({ pdfOptions, defaultPdfName }) => {
  // Debug: log des pdfOptions reçus
  console.log("PDFPositionTool - pdfOptions reçus:", pdfOptions);
  console.log("PDFPositionTool - clés pdfOptions:", Object.keys(pdfOptions));
  
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const [colorPickerOpen, setColorPickerOpen] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [hoverCoords, setHoverCoords] = useState<FieldData | null>(null);
  const [coordsByPage, setCoordsByPage] = useState<PDFCoordinatesByPage>({});
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState<string>(defaultPdfName || Object.keys(pdfOptions)[0]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: string } | null>(null);
  const [customTextField, setCustomTextField] = useState<string>("");
  const [showCustomTextInput, setShowCustomTextInput] = useState<boolean>(false);
  const [sourceDocumentForCopy, setSourceDocumentForCopy] = useState<string>("");
  const [draggedFieldKey, setDraggedFieldKey] = useState<string | null>(null);
  const [dragOverFieldKey, setDragOverFieldKey] = useState<string | null>(null);

  // Declare drawLabels first as it's used by renderPage
  const drawLabels = useCallback((ctx: CanvasRenderingContext2D, height: number, currentCoordsByPage: PDFCoordinatesByPage, currentPage: number) => {
    const pageCoords = currentCoordsByPage[currentPage];
    if (!pageCoords) return;

    const yOffsetPixels = mmToPixels(Y_OFFSET_CORRECTION);

    Object.entries(pageCoords).forEach(([key, { x, y, size = 12, color, bold, italic, customText, isCustomField }]) => {
      // Correction: Utilisation du bon nom de champ pour l'affichage
      const fieldName = key.includes('-') ? key.split('-')[1] : key;
      let fontStyle = '';
      if (bold) fontStyle += 'bold ';
      if (italic) fontStyle += 'italic ';
      ctx.font = `${fontStyle}${size}px Arial`;
      ctx.fillStyle = color || "#cc0077";
      // Simplification temporaire pour le débogage du PDF FDA
      // Affiche la coordonnée Y enregistrée (y=0 en haut) convertie pour le canvas (y=0 en bas)
      const isHillebrandPdf = selectedPdf === "hillebrandfdaregistration.pdf" || 
                              selectedPdf === "F_hillebrandfdaregistration.pdf" || 
                              selectedPdf === "REC_hillebrandfdaregistration.pdf";
      const adjustedY = isHillebrandPdf ? height - y + Y_OFFSET_TOOL_DISPLAY : height - y;
      
      // Afficher le texte personnalisé si c'est un champ personnalisé, sinon le nom du champ
      const displayText = isCustomField && customText ? customText : fieldName;
      
      ctx.fillText(
        displayText, 
        x, 
        isHillebrandPdf ? adjustedY - (size * PDF_FONT_SIZE_OFFSET) - yOffsetPixels : adjustedY - (size * PDF_FONT_SIZE_OFFSET) - yOffsetPixels
      );
    });
  }, [coordsByPage, currentPage, Y_OFFSET_CORRECTION, Y_OFFSET_TOOL_DISPLAY, PDF_FONT_SIZE_OFFSET, selectedPdf]); // Ajout de Y_OFFSET_TOOL_DISPLAY

  // Declare renderPage after drawLabels
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      const page = await pdfDoc.getPage(currentPage);
      const scale = PDF_SCALE;
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      if (!context) return;

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      if (renderTaskRef.current) renderTaskRef.current.cancel();

      const renderTask = page.render({
        canvasContext: context,
        viewport,
      });
      renderTaskRef.current = renderTask;

      await renderTask.promise;
      renderTaskRef.current = null;
      // drawLabels depends on coordsByPage, currentPage, etc. Pass them explicitly or ensure drawLabels has correct dependencies
      drawLabels(context, canvas.height, coordsByPage, currentPage);
    } catch (err) {
      if ((err as Error).name !== "RenderingCancelledException") {
        console.error("PDF rendering error:", err);
        toast.error("Erreur lors du rendu du PDF");
      }
    }
  }, [pdfDoc, currentPage, drawLabels, coordsByPage]); // Ajout de coordsByPage dans les dépendances de renderPage

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUserEmail(user?.email || null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Toujours charger depuis Firestore en priorité (source de vérité)
        if (userEmail) {
          const docName = pdfOptions[selectedPdf].firestoreDoc;
          const docRef = doc(db, "Coordinates", docName);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists() && docSnap.data()?.coordinates) {
            const firestoreData = docSnap.data().coordinates as PDFCoordinatesByPage;
            
            // Correction des noms de champs pour le PDF FDA et initialisation de l'ordre
            const processedData: PDFCoordinatesByPage = {};
            
            for (const page in firestoreData) {
              if (Object.prototype.hasOwnProperty.call(firestoreData, page)) {
                processedData[page] = {};
                const pageFields = Object.entries(firestoreData[page]);
                
                // Initialiser l'ordre si manquant
                pageFields.forEach(([key, value], index) => {
                  let correctedKey = key;
                  const isHillebrandPdf = selectedPdf === "hillebrandfdaregistration.pdf" || 
                                          selectedPdf === "F_hillebrandfdaregistration.pdf" || 
                                          selectedPdf === "REC_hillebrandfdaregistration.pdf";
                  if (isHillebrandPdf) {
                    correctedKey = key
                      .replace("hillebrand-companylName", "hillebrand-companyName")
                      .replace("hillebrand-registrationNumber", "hillebrand-address");
                  }
                  processedData[page][correctedKey] = {
                    ...value,
                    order: value.order !== undefined ? value.order : index
                  };
                });
              }
            }
            
            setCoordsByPage(processedData);
            // Mettre à jour le localStorage avec les données de Firestore
            localStorage.setItem("pdfCoordinatesByPage", JSON.stringify(processedData));
          } else {
            // Si Firestore est vide, initialiser avec un objet vide (ne pas charger depuis localStorage)
            setCoordsByPage({});
            // Nettoyer le localStorage pour éviter de charger les coordonnées d'un autre PDF
            localStorage.removeItem("pdfCoordinatesByPage");
          }
        } else {
          // Si pas connecté, utiliser localStorage
          const localData = localStorage.getItem("pdfCoordinatesByPage");
          if (localData) setCoordsByPage(JSON.parse(localData));
        }

        const loadingTask = getDocument(pdfOptions[selectedPdf].url);
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
      } catch (err) {
        console.error("Initial load error:", err);
        toast.error("Erreur lors du chargement initial");
      }
    };

    loadInitialData();
  }, [selectedPdf, userEmail, pdfOptions]);

  // Effect for rendering page and drawing labels
  useEffect(() => {
    renderPage();
  }, [pdfDoc, currentPage, drawLabels, renderPage]);

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.round(e.clientX - rect.left);
    const y = Math.round(e.clientY - rect.top);
    
    setHoverCoords({ x, y });
  };

  const isFieldUsed = (fieldName: string): boolean => {
    return coordsByPage[currentPage]?.[fieldName] !== undefined;
  };

  const sortedFields = useMemo(() => {
    let sortableFields = coordsByPage[currentPage] ? Object.entries(coordsByPage[currentPage]) : [];
    
    if (sortConfig !== null) {
      // Tri par colonne spécifique
      sortableFields.sort(([, a], [, b]) => {
        const aValue = a[sortConfig.key as keyof FieldData];
        const bValue = b[sortConfig.key as keyof FieldData];

        if (aValue === undefined || aValue === null) return 1;
        if (bValue === undefined || bValue === null) return -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
          // Fallback for other types, maybe compare as strings
          const aStr = String(aValue);
          const bStr = String(bValue);
          return sortConfig.direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
        }
      });
    } else {
      // Tri par ordre par défaut (order property)
      sortableFields.sort(([, a], [, b]) => {
        const orderA = a.order !== undefined ? a.order : 999999;
        const orderB = b.order !== undefined ? b.order : 999999;
        return orderA - orderB;
      });
    }
    return sortableFields;
  }, [coordsByPage, currentPage, sortConfig]);

  const requestSort = (key: string) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return '';
    }
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Mapping de tous les documents disponibles pour la copie
  const availableDocumentsForCopy: Record<string, string> = {
    "Devis REC TTB": "mondevispositions",
    "Devis REC FDA Enregistrement + Gestion": "fdaPositions",
    "Devis REC FDA Gestion": "fdaManagementPositions",
    "Devis REC Conseil réglementaire": "contratCadrePositions",
    "MonDevisTTB.pdf": "mondevispositions",
    "MonDevis.pdf": "pdfPositions",
    "hillerbrandfdaregistration.pdf": "fdaPositions",
    "F_hillebrandfdaregistration.pdf": "fdaPositionsFoodinnov",
    "REC_hillebrandfdaregistration.pdf": "fdaPositionsREC",
    "Facture.pdf": "maFacture",
    "FactureZak.pdf": "facturesZAK",
  };

  // Fonction pour copier les coordonnées d'un document source vers le document actuel
  const copyCoordinatesFromSource = async () => {
    if (!sourceDocumentForCopy) {
      toast.error("Veuillez sélectionner un document source");
      return;
    }

    if (!selectedPdf || !pdfOptions[selectedPdf]) {
      toast.error("Aucun document destination sélectionné");
      return;
    }

    const sourceFirestoreDoc = availableDocumentsForCopy[sourceDocumentForCopy];
    if (!sourceFirestoreDoc) {
      toast.error("Document source non trouvé");
      return;
    }

    try {
      // Charger les coordonnées du document source
      const sourceDocRef = doc(db, "Coordinates", sourceFirestoreDoc);
      const sourceDocSnap = await getDoc(sourceDocRef);

      if (!sourceDocSnap.exists() || !sourceDocSnap.data()?.coordinates) {
        toast.error("Le document source n'a pas de coordonnées");
        return;
      }

      const sourceCoordinates = sourceDocSnap.data().coordinates as PDFCoordinatesByPage;

      // Appliquer les coordonnées au document actuel
      setCoordsByPage(sourceCoordinates);
      localStorage.setItem("pdfCoordinatesByPage", JSON.stringify(sourceCoordinates));

      toast.success(`Coordonnées copiées depuis "${sourceDocumentForCopy}" vers "${selectedPdf}". Vous pouvez maintenant les modifier si nécessaire.`);
      
      // Le useEffect qui surveille coordsByPage va automatiquement recharger l'affichage
    } catch (error) {
      console.error("Erreur lors de la copie des coordonnées:", error);
      toast.error("Erreur lors de la copie des coordonnées");
    }
  };

  const saveCoordinatesToFirestore = async (coordinates: PDFCoordinatesByPage) => {
    if (!userEmail) {
      toast.error("Vous devez être connecté pour enregistrer");
      return;
    }

    if (!selectedPdf || !pdfOptions[selectedPdf]) {
      toast.error("Aucun PDF sélectionné");
      return;
    }

    setIsSaving(true);
    try {
      const docName = pdfOptions[selectedPdf].firestoreDoc;
      const coordinatesRef = doc(db, "Coordinates", docName);
      
      console.log(`🔄 Enregistrement en cours vers Firestore: ${docName}`);
      
      await setDoc(coordinatesRef, {
        coordinates,
        metadata: {
          lastUpdated: serverTimestamp(),
          updatedBy: userEmail,
          pdfName: selectedPdf
        }
      }, { merge: true });
      
      // Vérifier que l'enregistrement a bien été effectué
      const verifyDoc = await getDoc(coordinatesRef);
      if (!verifyDoc.exists()) {
        throw new Error("L'enregistrement n'a pas pu être vérifié");
      }
      
      // Log de la sauvegarde des coordonnées
      if (userEmail) {
        await logUserAction(
          userEmail,
          "Sauvegarde coordonnées PDF",
          `Coordonnées PDF sauvegardées pour ${selectedPdf} - Document: ${docName}`
        );
      }
      
      // Compter tous les champs de toutes les pages
      const totalFieldCount = Object.values(coordinates).reduce((total, pageCoords) => {
        return total + Object.keys(pageCoords).length;
      }, 0);
      
      console.log(`✅ Enregistrement réussi: ${docName} - ${totalFieldCount} champ(s) sauvegardé(s) sur ${Object.keys(coordinates).length} page(s)`);
      toast.success(
        `✅ Enregistrement réussi !\n${totalFieldCount} champ(s) sauvegardé(s) dans "${docName}"`,
        { autoClose: 4000 }
      );
    } catch (error: any) {
      console.error("❌ Erreur d'enregistrement :", error);
      const errorMessage = error?.message || "Une erreur inconnue s'est produite";
      toast.error(
        `❌ Échec de l'enregistrement\n${errorMessage}`,
        { autoClose: 5000 }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const updateCoord = (key: string, field: keyof FieldData, value: number | string | boolean) => {
    const existingField = coordsByPage[currentPage]?.[key];
    const updated = {
      ...coordsByPage,
      [currentPage]: {
        ...coordsByPage[currentPage],
        [key]: {
          ...(existingField || { x: 0, y: 0, size: 12 }),
          [field]: value,
          // Préserver l'ordre si le champ existe déjà
          order: existingField?.order !== undefined ? existingField.order : (existingField ? Object.keys(coordsByPage[currentPage] || {}).length : 0)
        },
      },
    };
    setCoordsByPage(updated);
    localStorage.setItem("pdfCoordinatesByPage", JSON.stringify(updated));
  };

  const updateCustomText = (key: string, newText: string) => {
    const updated = {
      ...coordsByPage,
      [currentPage]: {
        ...coordsByPage[currentPage],
        [key]: {
          ...coordsByPage[currentPage][key],
          customText: newText,
        },
      },
    };
    setCoordsByPage(updated);
    localStorage.setItem("pdfCoordinatesByPage", JSON.stringify(updated));
    toast.success("Texte personnalisé mis à jour");
  };

  const handleColorChange = (key: string, color: ColorResult) => {
    updateCoord(key, "color", color.hex);
    setColorPickerOpen(null);
  };

  const removeField = async (key: string) => {
    const updated = { ...coordsByPage };
    if (updated[currentPage]?.[key]) {
      delete updated[currentPage][key];
      setCoordsByPage(updated);
      localStorage.setItem("pdfCoordinatesByPage", JSON.stringify(updated));
      
      // Mettre à jour Firestore après suppression
      if (userEmail) {
        try {
          const docName = pdfOptions[selectedPdf].firestoreDoc;
          const docRef = doc(db, "Coordinates", docName);
          
          // Utiliser setDoc sans merge pour remplacer complètement l'objet coordinates
          // Cela garantit que les champs supprimés sont bien retirés
          await setDoc(docRef, {
            coordinates: updated,
            metadata: {
              lastUpdated: serverTimestamp(),
              updatedBy: userEmail,
              pdfName: selectedPdf
            }
          }, { merge: false }); // merge: false pour remplacer complètement les coordinates
          
          // Log de la suppression du champ
          if (userEmail) {
            await logUserAction(
              userEmail,
              "Suppression champ PDF",
              `Champ PDF supprimé: ${key.split('-')[1]} pour ${selectedPdf}`
            );
          }
          
          toast.success(`Champ "${key.split('-')[1]}" supprimé de Firestore`);
        } catch (err) {
          console.error("Erreur suppression champ Firestore :", err);
          toast.error("Erreur lors de la suppression du champ de Firestore.");
        }
      } else {
        toast.success(`Champ "${key.split('-')[1]}" supprimé (local seulement)`);
      }
    }
  };

  const addFieldFromHover = async () => {
    if (!selectedField || !hoverCoords) return;

    const isExistingField = isFieldUsed(selectedField);

    if (isExistingField && !window.confirm(
      `Ce champ est déjà positionné. Voulez-vous le déplacer à X=${hoverCoords.x}, Y=${hoverCoords.y} ?`
    )) return;

    const pageCoords = coordsByPage[currentPage] || {};
    const maxOrder = Math.max(...Object.values(pageCoords).map(f => f.order ?? -1), -1);
    const newOrder = isExistingField ? (pageCoords[selectedField]?.order ?? maxOrder + 1) : maxOrder + 1;

    const updated = {
      ...coordsByPage,
      [currentPage]: {
        ...coordsByPage[currentPage],
        [selectedField]: {
          ...(coordsByPage[currentPage]?.[selectedField] || { 
            size: 12, 
            color: "#cc0077",
            bold: false,
            italic: false
          }),
          x: hoverCoords.x,
          // Enregistre la coordonnée Y du curseur désactivée par l'échelle du PDF (Y=0 en haut sur PDF original)
          y: Math.round(hoverCoords.y / PDF_SCALE),
          order: newOrder
        },
      },
    };
    
    setCoordsByPage(updated);
    localStorage.setItem("pdfCoordinatesByPage", JSON.stringify(updated));
    toast.success(isExistingField 
      ? `Position du champ "${selectedField.split('-')[1]}" mise à jour`
      : `Champ "${selectedField.split('-')[1]}" ajouté`
    );
    setSelectedField(null);
  };

  const addCustomTextField = async () => {
    if (!customTextField.trim() || !hoverCoords) return;

    const customFieldName = `custom-${Date.now()}`;
    const isExistingField = isFieldUsed(customFieldName);

    if (isExistingField && !window.confirm(
      `Ce champ est déjà positionné. Voulez-vous le déplacer à X=${hoverCoords.x}, Y=${hoverCoords.y} ?`
    )) return;

    const pageCoords = coordsByPage[currentPage] || {};
    const maxOrder = Math.max(...Object.values(pageCoords).map(f => f.order ?? -1), -1);
    const newOrder = isExistingField ? (pageCoords[customFieldName]?.order ?? maxOrder + 1) : maxOrder + 1;

    const updated = {
      ...coordsByPage,
      [currentPage]: {
        ...coordsByPage[currentPage],
        [customFieldName]: {
          x: hoverCoords.x,
          y: Math.round(hoverCoords.y / PDF_SCALE),
          size: 12,
          color: "#cc0077",
          bold: false,
          italic: false,
          customText: customTextField.trim(),
          isCustomField: true,
          order: newOrder
        },
      },
    };
    
    setCoordsByPage(updated);
    localStorage.setItem("pdfCoordinatesByPage", JSON.stringify(updated));
    toast.success(`Champ texte personnalisé "${customTextField}" ajouté`);
    setCustomTextField("");
    setShowCustomTextInput(false);
  };

  // Fonction pour réorganiser les champs dans le tableau
  const reorderFields = (draggedKey: string, targetKey: string) => {
    const pageCoords = coordsByPage[currentPage];
    if (!pageCoords) return;

    // Trier les champs par ordre existant
    const entries = Object.entries(pageCoords).sort(([, a], [, b]) => {
      const orderA = a.order !== undefined ? a.order : 999999;
      const orderB = b.order !== undefined ? b.order : 999999;
      return orderA - orderB;
    });

    const draggedIndex = entries.findIndex(([key]) => key === draggedKey);
    const targetIndex = entries.findIndex(([key]) => key === targetKey);

    if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) return;

    // Réorganiser les entrées
    const reorderedEntries = [...entries];
    const [draggedEntry] = reorderedEntries.splice(draggedIndex, 1);
    reorderedEntries.splice(targetIndex, 0, draggedEntry);

    // Reconstruire l'objet avec les nouveaux ordres
    const reorderedCoords: Record<string, FieldData> = {};
    reorderedEntries.forEach(([key, value], index) => {
      reorderedCoords[key] = { ...value, order: index };
    });

    // Mettre à jour l'état
    const updated = {
      ...coordsByPage,
      [currentPage]: reorderedCoords
    };
    setCoordsByPage(updated);
    localStorage.setItem("pdfCoordinatesByPage", JSON.stringify(updated));
  };

  const resetAllCoordinates = async () => {
    if (!window.confirm("Supprimer toutes les coordonnées enregistrées ?")) return;

    try {
      localStorage.removeItem("pdfCoordinatesByPage");
      if (userEmail) {
        const docName = pdfOptions[selectedPdf].firestoreDoc;
        await setDoc(doc(db, "Coordinates", docName), {
          coordinates: {},
          metadata: {
            lastUpdated: serverTimestamp(),
            updatedBy: userEmail
          }
        });
      }
      setCoordsByPage({});
      toast.success("Coordonnées réinitialisées");
    } catch (err) {
      console.error("Erreur lors de la réinitialisation :", err);
      toast.error("Erreur lors de la suppression des coordonnées");
    }
  };

  const handlePdfChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    
    // Si le PDF sélectionné est dans les pdfOptions de la page actuelle, on le change localement
    if (pdfOptions[value]) {
      // Le changement sera géré par le onChange du select qui appelle setSelectedPdf
      return;
    }
    
    // Sinon, naviguer vers une autre page (pour les PDFs d'autres outils)
    switch(value) {
      case 'MonDevis.pdf':
        navigate('/admin/pdf-tool');
        break;
      case 'MonDevisTTB.pdf':
        navigate('/admin/pdf-tool-devis');
        break;
      case 'Devis REC TTB':
        // Déjà sur la bonne page, changement local géré par setSelectedPdf
        break;
      case 'Devis REC FDA Enregistrement + Gestion':
        // Déjà sur la bonne page, changement local géré par setSelectedPdf
        break;
      case 'Devis REC FDA Gestion':
        // Déjà sur la bonne page, changement local géré par setSelectedPdf
        break;
      case 'DevisFoodinnov.pdf':
        // TODO: Ajouter la redirection vers la page de devis Foodinnov quand elle sera créée
        console.log('Redirection vers devis Foodinnov à implémenter');
        break;
      case 'hillerbrandfdaregistration.pdf':
        navigate('/outil-fda-pdf');
        break;
      case 'Facture.pdf':
        navigate('/outil-facture-pdf');
        break;
      default:
        // Gérer le cas où l'option vide est sélectionnée (ne rien faire ou rediriger vers une page d'accueil de l'outil)
        break;
    }
  };

  const fieldGroups = selectedPdf 
    ? Object.entries(pdfOptions[selectedPdf].fields).map(([source, fields]) => (
        <optgroup key={source} label={source.charAt(0).toUpperCase() + source.slice(1)}>
          {fields.map((field) => {
            const fullFieldName = `${source}-${field}`;
            const isUsed = isFieldUsed(fullFieldName);
            return (
              <option 
                key={fullFieldName} 
                value={fullFieldName}
                className={isUsed ? "field-used" : "field-available"}
              >
                {field} {isUsed && "(déjà utilisé)"}
              </option>
            );
          })}
        </optgroup>
      ))
    : null;

  return (
    <div className="pdf-position-tool-container">
      <h2>Outil de Repérage PDF</h2>
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <select
          value={selectedPdf}
          onChange={(e) => {
            setSelectedPdf(e.target.value);
            handlePdfChange(e);
          }}
          className="pdf-rep-select"
        >
          <option value="">Sélectionnez un type de document</option>
          {/* PDFs locaux (dynamiques depuis pdfOptions) - TOUJOURS affichés en premier */}
          {Object.keys(pdfOptions).map((pdfName) => (
            <option key={`local-${pdfName}`} value={pdfName}>
              {pdfName}
            </option>
          ))}
          {/* PDFs des autres outils (navigation) - seulement si pas déjà dans pdfOptions */}
          {!("MonDevis.pdf" in pdfOptions) && <option key="nav-MonDevis.pdf" value="MonDevis.pdf">Mon Devis</option>}
          {!("MonDevisTTB.pdf" in pdfOptions) && !("Devis REC TTB" in pdfOptions) && <option key="nav-MonDevisTTB.pdf" value="MonDevisTTB.pdf">Mon Devis TTB</option>}
          {!("hillerbrandfdaregistration.pdf" in pdfOptions) && <option key="nav-hillerbrand" value="hillerbrandfdaregistration.pdf">Hillerbrand FDA</option>}
          {!("Facture.pdf" in pdfOptions) && <option key="nav-Facture" value="Facture.pdf">Ma Facture</option>}
        </select>
      </div>
      <p className="pdf-instruction">
        Cliquez sur le PDF pour obtenir les coordonnées <code>X / Y</code> et modifiez-les à droite.
      </p>

      <div className="pdf-body">
        <div className="pdf-viewer-container">
          <canvas
            ref={canvasRef}
            onMouseMove={handleMouseMove}
            className="pdf-canvas"
          />
        </div>

        <div className="pdf-coord-fields">
          {totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}>
                ◀
              </button>
              <span>Page {currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}>
                ▶
              </button>
            </div>
          )}

<br/>

          <div className="pdf-coord-fields-content">
            {hoverCoords && (
              <div className="pdf-coordinates">
                Curseur : <strong>X = {hoverCoords.x}</strong>,{" "}
                <strong>Y = {hoverCoords.y}</strong>
              </div>
            )}

            <h3>Coordonnées des champs PDF</h3>
            <table className="coords-table">
              <thead>
                <tr>
                  <th>Champ</th>
                  <th>X</th>
                  <th>Y</th>
                  <th>Taille</th>
                  <th>Couleur</th>
                  <th>Gras</th>
                  <th>Italic</th>
                  <th>Suppr.</th>
                </tr>
              </thead>
              <tbody>
                {sortedFields
                  .filter(([key]) => {
                    const isHillebrandPdf = selectedPdf === "hillebrandfdaregistration.pdf" || 
                                            selectedPdf === "F_hillebrandfdaregistration.pdf" || 
                                            selectedPdf === "REC_hillebrandfdaregistration.pdf";
                    if (isHillebrandPdf) {
                      return key.startsWith("hillebrand-");
                    }
                    return true;
                  })
                  .map(([key, { x, y, size, color, bold, italic, customText, isCustomField }]) => (
                    <tr 
                      key={key}
                      draggable
                      onDragStart={(e) => {
                        setDraggedFieldKey(key);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        setDragOverFieldKey(key);
                      }}
                      onDragLeave={() => {
                        setDragOverFieldKey(null);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (draggedFieldKey && draggedFieldKey !== key) {
                          reorderFields(draggedFieldKey, key);
                        }
                        setDraggedFieldKey(null);
                        setDragOverFieldKey(null);
                      }}
                      onDragEnd={() => {
                        setDraggedFieldKey(null);
                        setDragOverFieldKey(null);
                      }}
                      style={{
                        opacity: draggedFieldKey === key ? 0.5 : 1,
                        backgroundColor: dragOverFieldKey === key ? '#e3f2fd' : 'transparent',
                        cursor: 'move',
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <td style={{ cursor: 'grab' }} title="Glissez pour réorganiser">
                        <span style={{ marginRight: '8px', fontSize: '1.2em', userSelect: 'none' }}>☰</span>
                        {isCustomField ? (
                          <div>
                            <strong>Texte personnalisé:</strong><br/>
                            <input
                              type="text"
                              value={customText || ""}
                              onChange={(e) => updateCustomText(key, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '2px 4px',
                                fontSize: '0.8em',
                                border: '1px solid #ddd',
                                borderRadius: '2px',
                                marginTop: '2px'
                              }}
                              placeholder="Entrez votre texte"
                            />
                          </div>
                        ) : (
                          key.includes('-') ? key.split('-')[1] : key
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          value={x}
                          onChange={(e) => updateCoord(key, "x", parseInt(e.target.value) || 0)}
                          placeholder="X"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={y}
                          onChange={(e) => updateCoord(key, "y", parseInt(e.target.value) || 0)}
                          placeholder="Y"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={size ?? 12}
                          onChange={(e) => updateCoord(key, "size", parseInt(e.target.value) || 12)}
                          placeholder="Taille"
                        />
                      </td>
                      <td>
                        <div className="color-picker-container">
                          <div
                            className="color-preview"
                            style={{ backgroundColor: color || "#cc0077" }}
                            onClick={() => setColorPickerOpen(key)}
                          />
                          {colorPickerOpen === key && (
                            <div className="color-popover">
                              <div
                                className="color-cover"
                                onClick={() => setColorPickerOpen(null)}
                              />
                              <ChromePicker
                                color={color || "#cc0077"}
                                onChangeComplete={(color) => handleColorChange(key, color)}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <label className="style-checkbox">
                          <input
                            type="checkbox"
                            checked={!!bold}
                            onChange={(e) => updateCoord(key, "bold", e.target.checked)}
                          />
                        </label>
                      </td>
                      <td>
                         <label className="style-checkbox">
                          <input
                            type="checkbox"
                            checked={!!italic}
                            onChange={(e) => updateCoord(key, "italic", e.target.checked)}
                          />
                        </label>
                      </td>
                      <td>
                        <button
                          className="remove-field-btn"
                          onClick={() => removeField(key)}
                          title="Supprimer ce champ"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
    
            <div className="pdf-field-adder">
              <select
                value={selectedField || ""}
                onChange={(e) => setSelectedField(e.target.value || null)}
              >
                <option value="">-- Ajouter un champ --</option>
                {fieldGroups}
              </select>
              
              <button
                onClick={addFieldFromHover}
                disabled={!hoverCoords || !selectedField}
              >
                ➕ {selectedField ? `Ajouter/mettre à jour ${selectedField.split('-')[1]}` : "Ajouter un champ"}
              </button>

              <div style={{ marginTop: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#2c3e50' }}>Ajouter un texte personnalisé</h4>
                
                {!showCustomTextInput ? (
                  <button
                    onClick={() => setShowCustomTextInput(true)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      backgroundColor: '#3498db',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    📝 Ajouter du texte personnalisé (ex: "€", "TTC", etc.)
                  </button>
                ) : (
                  <div>
                    <input
                      type="text"
                      value={customTextField}
                      onChange={(e) => setCustomTextField(e.target.value)}
                      placeholder="Entrez votre texte (ex: €, TTC, HT, etc.)"
                      style={{
                        width: '100%',
                        padding: '8px',
                        marginBottom: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '0.8rem'
                      }}
                    />
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={addCustomTextField}
                        disabled={!hoverCoords || !customTextField.trim()}
                        style={{
                          flex: 1,
                          padding: '8px',
                          backgroundColor: '#2ecc71',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ➕ Ajouter le texte
                      </button>
                      <button
                        onClick={() => {
                          setShowCustomTextInput(false);
                          setCustomTextField("");
                        }}
                        style={{
                          flex: 1,
                          padding: '8px',
                          backgroundColor: '#95a5a6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ✕ Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '20px', padding: '15px', border: '2px solid #3498db', borderRadius: '4px', backgroundColor: '#ecf0f1' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#2c3e50' }}>📋 Copier les coordonnées d'un autre document</h4>
                <p style={{ fontSize: '0.8rem', color: '#7f8c8d', marginBottom: '10px' }}>
                  Sélectionnez un document source pour copier toutes ses coordonnées vers le document actuel ({selectedPdf})
                </p>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <select
                    value={sourceDocumentForCopy}
                    onChange={(e) => setSourceDocumentForCopy(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="">-- Sélectionnez un document source --</option>
                    {Object.keys(availableDocumentsForCopy).map((docName) => (
                      <option key={docName} value={docName}>
                        {docName}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={copyCoordinatesFromSource}
                    disabled={!sourceDocumentForCopy || sourceDocumentForCopy === selectedPdf}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: sourceDocumentForCopy && sourceDocumentForCopy !== selectedPdf ? '#3498db' : '#95a5a6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: sourceDocumentForCopy && sourceDocumentForCopy !== selectedPdf ? 'pointer' : 'not-allowed',
                      fontSize: '0.9rem',
                      fontWeight: 'bold'
                    }}
                    title={sourceDocumentForCopy === selectedPdf ? "Vous ne pouvez pas copier depuis le même document" : "Copier les coordonnées"}
                  >
                    📋 Copier
                  </button>
                </div>
              </div>
              
              <div className="pdf-actions" style={{ marginTop: '15px' }}>
                <button 
                  onClick={() => saveCoordinatesToFirestore(coordsByPage)}
                  disabled={isSaving || !userEmail}
                  className="save-button"
                >
                  {isSaving ? "Enregistrement..." : "Enregistrer dans Firestore"}
                </button>
                
                {!userEmail && (
                  <p className="auth-warning">
                    Connectez-vous pour activer l'enregistrement
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFPositionTool;